<template>
  <div class="product-ranks-container">
    <!-- 页面标题 -->
    <h4 class="page-title">订单签收率统计</h4>
    
    <!-- 日期筛选 -->
    <div class="date-filter-section">
      <div class="filter-row">
        <span class="filter-label">订单时间</span>
        <input 
          type="date" 
          v-model="searchParams.startDate" 
          class="date-input"
          :disabled="loading"
        />
        <span class="date-separator">至</span>
        <input 
          type="date" 
          v-model="searchParams.endDate" 
          class="date-input"
          :disabled="loading"
        />
        <el-button 
          type="primary" 
          @click="fetchData" 
          :loading="loading"
          :disabled="loading"
          class="search-btn"
        >
          查询
        </el-button>
      </div>
    </div>
    
    <el-divider />

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading loading-icon"></i>
      <span>数据加载中...</span>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <el-alert
        :title="error"
        type="error"
        show-icon
        :closable="false"
      />
      <div class="error-retry">
        <el-button type="primary" @click="fetchData">重新加载</el-button>
      </div>
    </div>

    <!-- 统计卡片区域 -->
    <div v-else class="stats-section">
      <el-row :gutter="20">
        <!-- 总订单统计 -->
        <el-col :xs="24" :sm="12" :md="8">
          <div class="stat-card">
            <h5 class="stat-header">总订单量</h5>
            <div class="stat-content">
              <div class="stat-item">
                <div class="stat-label">总订单</div>
                <div class="stat-value">{{ stats.totalOrderNum || '0' }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">签收单</div>
                <div class="stat-value">{{ stats.signOrderNum || '0' }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">签收率</div>
                <div class="stat-value">{{ stats.signRateStr || '0%' }}</div>
              </div>
            </div>
          </div>
        </el-col>

        <!-- 公品订单统计 -->
        <el-col :xs="24" :sm="12" :md="8">
          <div class="stat-card">
            <h5 class="stat-header">公品订单量</h5>
            <div class="stat-content">
              <div class="stat-item">
                <div class="stat-label">总订单</div>
                <div class="stat-value">{{ stats.publicTotalOrderNum || '0' }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">签收单</div>
                <div class="stat-value">{{ stats.publicSignOrderNum || '0' }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">签收率</div>
                <div class="stat-value">{{ stats.publicSignRateStr || '0%' }}</div>
              </div>
            </div>
          </div>
        </el-col>

        <!-- 公品贡献榜 -->
        <el-col :xs="24" :sm="24" :md="8">
          <div class="stat-card">
            <h5 class="stat-header">公品贡献榜</h5>
            <div class="leaderboard" v-if="stats.contributionList && stats.contributionList.length > 0">
              <div 
                v-for="(item, index) in stats.contributionList" 
                :key="index"
                class="leaderboard-item"
              >
                <div class="leaderboard-rank">
                  {{ getRankText(index) }}
                </div>
                <div class="leaderboard-name">{{ item.name }}</div>
                <div class="leaderboard-count">{{ item.count }} 单</div>
              </div>
            </div>
            <div v-else class="empty-leaderboard">
              暂无贡献数据
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <el-divider />

    <!-- 三个排名表格并排显示 -->
    <div v-if="!loading && !error" class="ranking-section">
      <el-row :gutter="20">
        <!-- 公品单量排名 -->
        <el-col :xs="24" :sm="24" :md="8">
          <div class="ranking-card">
            <div class="ranking-header">
              <h5 class="ranking-title">公品单量排名</h5>
              <div class="ranking-actions">
                <span class="ranking-subtitle">最近30天</span>
                <el-link 
                  type="primary" 
                  @click="toggleShowAll('publicProduct')"
                  class="ranking-more"
                >
                  {{ showAll.publicProduct ? '收起' : '更多' }}
                </el-link>
              </div>
            </div>
            <table class="ranking-table">
              <thead>
                <tr>
                  <th>产品名称</th>
                  <th>SKU</th>
                  <th>订单量</th>
                  <th>总签收率</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in getRankingData('publicProduct')" :key="index">
                  <td>{{ item.skuName || '-' }}</td>
                  <td>{{ item.skuCode || '-' }}</td>
                  <td style="text-align: right;">{{ item.totalOrderNum || '0' }}</td>
                  <td style="text-align: right;">{{ item.signRateStr || '0%' }}</td>
                </tr>
                <tr v-if="getRankingData('publicProduct').length === 0">
                  <td colspan="4" style="text-align: center; padding: 20px;">暂无数据</td>
                </tr>
                <tr v-if="shouldShowPlaceholder('publicProduct')">
                  <td :colspan="getPlaceholderColspan('publicProduct')" style="text-align: center; padding: 10px; color: #909399; font-style: italic;">
                    {{ getPlaceholderText('publicProduct') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </el-col>

        <!-- 业务员排名 -->
        <el-col :xs="24" :sm="24" :md="8">
          <div class="ranking-card">
            <div class="ranking-header">
              <h5 class="ranking-title">业务员排名</h5>
              <div class="ranking-actions">
                <span class="ranking-subtitle">最近30天</span>
                <el-link 
                  type="primary" 
                  @click="toggleShowAll('salesman')"
                  class="ranking-more"
                >
                  {{ showAll.salesman ? '收起' : '更多' }}
                </el-link>
              </div>
            </div>
            <table class="ranking-table">
              <thead>
                <tr>
                  <th>业务员</th>
                  <th>总单量</th>
                  <th>总签收率</th>
                  <th>公品单量</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in getRankingData('salesman')" :key="index">
                  <td>{{ item.salesman || '-' }}</td>
                  <td style="text-align: right;">{{ item.totalOrderNum || '0' }}</td>
                  <td style="text-align: right;">{{ item.signRateStr || '0%' }}</td>
                  <td style="text-align: right;">{{ item.publicSkuNum || '0' }}</td>
                </tr>
                <tr v-if="getRankingData('salesman').length === 0">
                  <td colspan="4" style="text-align: center; padding: 20px;">暂无数据</td>
                </tr>
                <tr v-if="shouldShowPlaceholder('salesman')">
                  <td :colspan="getPlaceholderColspan('salesman')" style="text-align: center; padding: 10px; color: #909399; font-style: italic;">
                    {{ getPlaceholderText('salesman') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </el-col>

        <!-- 一级类目单量排名 -->
        <el-col :xs="24" :sm="24" :md="8">
          <div class="ranking-card">
            <div class="ranking-header">
              <h5 class="ranking-title">一级类目单量排名</h5>
              <div class="ranking-actions">
                <span class="ranking-subtitle">最近30天</span>
                <el-link 
                  type="primary" 
                  @click="toggleShowAll('category')"
                  class="ranking-more"
                >
                  {{ showAll.category ? '收起' : '更多' }}
                </el-link>
              </div>
            </div>
            <table class="ranking-table">
              <thead>
                <tr>
                  <th>项目名称</th>
                  <th>订单量</th>
                  <th>签收率</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in getRankingData('category')" :key="index">
                  <td>{{ item.categorize || '-' }}</td>
                  <td style="text-align: right;">{{ item.totalOrderNum || '0' }}</td>
                  <td style="text-align: right;">{{ item.signRateStr || '0%' }}</td>
                </tr>
                <tr v-if="getRankingData('category').length === 0">
                  <td colspan="3" style="text-align: center; padding: 20px;">暂无数据</td>
                </tr>
                <tr v-if="shouldShowPlaceholder('category')">
                  <td :colspan="getPlaceholderColspan('category')" style="text-align: center; padding: 10px; color: #909399; font-style: italic;">
                    {{ getPlaceholderText('category') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductRanks',
  data() {
    // 设置默认日期范围（最近30天）
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };

    return {
      searchParams: {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate)
      },
      loading: false,
      error: null,
      stats: {
        totalOrderNum: 0,
        signOrderNum: 0,
        signRateStr: '0%',
        publicTotalOrderNum: 0,
        publicSignOrderNum: 0,
        publicSignRateStr: '0%',
        contributionList: [],
        publicProductRanking: [],
        salesmanRanking: [],
        categoryRanking: []
      },
      showAll: {
        publicProduct: false,
        salesman: false,
        category: false
      }
    };
  },
  methods: {
    // 获取排名文本
    getRankText(index) {
      const rankTexts = ['第一名：', '第二名：', '第三名：'];
      return rankTexts[index] || `${index + 1}名：`;
    },

    // 切换显示全部数据
    toggleShowAll(type) {
      this.showAll[type] = !this.showAll[type];
    },

    // 获取排名数据（根据是否显示全部）
    getRankingData(type) {
      const data = this.stats[`${type}Ranking`] || [];
      const showAll = this.showAll[type];
      
      if (showAll || data.length <= 10) {
        return data;
      } else {
        return data.slice(0, 10);
      }
    },
    
    // 检查是否需要显示占位符
    shouldShowPlaceholder(type) {
      const data = this.stats[`${type}Ranking`] || [];
      const showAll = this.showAll[type];
      return !showAll && data.length > 10;
    },
    
    // 获取占位符文本
    getPlaceholderText(type) {
      const data = this.stats[`${type}Ranking`] || [];
      return `还有 ${data.length - 10} 条数据未显示`;
    },
    
    // 获取占位符列数
    getPlaceholderColspan(type) {
      return type === 'publicProduct' ? 4 : type === 'salesman' ? 4 : 3;
    },

    // 获取数据
    async fetchData() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = {
          startDate: this.searchParams.startDate,
          endDate: this.searchParams.endDate
        };

        const response = await this.axios.get('/data-serv/product/sign-rate', { params });
        
        if (response.data.code === 0 && response.data.data) {
          this.stats = {
            ...this.stats,
            ...response.data.data
          };
        } else {
          throw new Error(response.data.message || '数据加载失败');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.error = error.message || '数据加载失败';
        this.$message.error('数据加载失败: ' + error.message);
        
        // 清空数据
        this.stats = {
          totalOrderNum: 0,
          signOrderNum: 0,
          signRateStr: '0%',
          publicTotalOrderNum: 0,
          publicSignOrderNum: 0,
          publicSignRateStr: '0%',
          contributionList: [],
          publicProductRanking: [],
          salesmanRanking: [],
          categoryRanking: []
        };
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    // 组件挂载时获取数据
    this.fetchData();
  }
};
</script>

<style scoped>
.product-ranks-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
}

/* 日期筛选区域 */
.date-filter-section {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-label {
  font-weight: 500;
  color: #606266;
  min-width: 60px;
}

.date-input {
  width: 180px;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  box-sizing: border-box;
}

.date-input:focus {
  border-color: #409eff;
  outline: none;
}

.date-input:disabled {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
}

.date-separator {
  color: #909399;
}

.search-btn {
  margin-left: 10px;
}

/* 统计卡片区域 */
.stats-section {
  margin-bottom: 30px;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  height: 100%;
}

.stat-header {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 8px;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  font-size: 14px;
  color: #606266;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

/* 贡献榜样式 */
.leaderboard {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.leaderboard-item {
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.leaderboard-item:last-child {
  border-bottom: none;
}

.leaderboard-rank {
  font-weight: 600;
  color: #909399;
  min-width: 80px;
}

.leaderboard-name {
  flex-grow: 1;
  color: #303133;
}

.leaderboard-count {
  font-weight: 600;
  color: #409eff;
}

.empty-leaderboard {
  text-align: center;
  color: #909399;
  padding: 20px 0;
  font-size: 14px;
}

/* 排名卡片区域 */
.ranking-section {
  margin-top: 20px;
}

.ranking-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  height: 100%;
}

.ranking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.ranking-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.ranking-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ranking-subtitle {
  font-size: 14px;
  color: #909399;
}

.ranking-more {
  font-size: 14px;
}

.ranking-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.ranking-table th,
.ranking-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
}

.ranking-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.ranking-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

.ranking-table tbody tr:hover {
  background-color: #f5f5f5;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .product-ranks-container {
    padding: 15px;
  }
  
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-label {
    margin-bottom: 8px;
  }
  
  .date-input {
    width: 100%;
    margin-bottom: 10px;
  }
  
  .date-separator {
    display: none;
  }
  
  .search-btn {
    margin-left: 0;
    margin-top: 10px;
  }
  
  .ranking-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .ranking-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .ranking-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
  
  .ranking-table th,
  .ranking-table td {
    padding: 6px 4px;
    font-size: 14px;
  }
}

@media (max-width: 1200px) {
  .ranking-section .el-col {
    margin-bottom: 20px;
  }
  
  .ranking-section .el-col:last-child {
    margin-bottom: 0;
  }
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.loading-icon {
  font-size: 40px;
  color: #409eff;
  margin-bottom: 16px;
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-container span {
  font-size: 16px;
  color: #606266;
}

/* 错误状态样式 */
.error-container {
  padding: 20px;
}

.error-retry {
  margin-top: 20px;
  text-align: center;
}
</style>